import React, { useEffect, useState } from "react";
import { MessageSquarePlus, Loader2, Check, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import api from "../api";
import { useLang } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";

const TYPES = [
  { id: "bug", k: "fb.typeBug" },
  { id: "idea", k: "fb.typeIdea" },
  { id: "compliment", k: "fb.typeCompliment" },
  { id: "other", k: "fb.typeOther" },
];

// Feedback has to be one tap from wherever they are, and never behind an account — the moment
// you make someone log in to tell you something is broken, they just leave instead.
export const FeedbackButton = ({ raised = false }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [context, setContext] = useState(true);
  const [busy, setBusy] = useState(false);
  // The cookie banner lands in the same corner on a first visit, so step out of its way.
  const [banner, setBanner] = useState(false);
  useEffect(() => {
    const check = () => { try { setBanner(!localStorage.getItem("lp-cookie-consent")); } catch { setBanner(false); } };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, []);

  // Only what helps reproduce a bug, and only if they leave the box ticked.
  const deviceLine = () => {
    const mobile = /iphone|ipad|android/i.test(navigator.userAgent);
    return `${mobile ? "Phone" : "Computer"} · ${window.innerWidth}×${window.innerHeight} · ${navigator.language} · ${navigator.userAgent.slice(0, 180)}`;
  };

  const send = async () => {
    if (message.trim().length < 5) { toast({ title: t("fb.tooShort") }); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, message: message.trim(), email: email.trim() || undefined,
        path: context ? pathname : undefined, device: context ? deviceLine() : undefined });
      setOpen(false); setMessage(""); setEmail(""); setType("idea");
      toast({ title: t("fb.sent"), description: t("fb.sentNote") });
    } catch (e) { toast({ title: t("fb.failed"), description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-open"
        aria-label={t("fb.button")}
        className={`fixed right-5 z-40 h-12 w-12 rounded-full bg-[#0f2a1a] hover:bg-[#16341f] text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 ${banner ? "bottom-[max(14rem,calc(env(safe-area-inset-bottom)+14rem))] sm:bottom-[max(12rem,calc(env(safe-area-inset-bottom)+12rem))]" : raised ? "bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5.5rem))] sm:bottom-5" : "bottom-[max(1.25rem,env(safe-area-inset-bottom))]"}`}>
        <MessageSquarePlus size={20} className="text-[#f5b942]" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50" data-testid="feedback-sheet">
          <div className="w-full max-w-md bg-card border border-border rounded-3xl p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-extrabold text-foreground">{t("fb.title")}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{t("fb.sub")}</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="feedback-close" aria-label={t("pc.remove")}
                className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"><X size={15} /></button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {TYPES.map((o) => (
                <button key={o.id} onClick={() => setType(o.id)} data-testid={`feedback-type-${o.id}`}
                  className={`text-xs font-bold rounded-full px-3 py-2 border transition-colors ${type === o.id ? "bg-[#16a34a] border-[#16a34a] text-white" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                  {t(o.k)}
                </button>
              ))}
            </div>

            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="feedback-message"
              placeholder={t("fb.messagePlaceholder")} maxLength={3000}
              className="mt-3 w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-[#16a34a]/40" />

            <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="feedback-email"
              placeholder={t("fb.emailLabel")} type="email"
              className="mt-2 w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />

            <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid="feedback-context-label">
              <input type="checkbox" checked={context} onChange={(e) => setContext(e.target.checked)}
                data-testid="feedback-context" className="mt-0.5 h-4 w-4 accent-[#16a34a]" />
              <span className="text-xs text-muted-foreground">
                {t("fb.context")}
                <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{context ? t("fb.contextOn", { path: pathname }) : t("fb.contextOff")}</span>
              </span>
            </label>

            <button onClick={send} disabled={busy} data-testid="feedback-send"
              className="mt-3 w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} {t("fb.send")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
